<template>
  <div class="config-import-manual">
    <p class="font-weight-bold">{{ $t('audience-new.config-import.manual.description') }} <span class="text-danger">*</span></p>
    <CTextarea
      v-model="importValue"
      :placeholder="textAreaPlaceholder"
      :disabled="isLoadingStep"
      addLabelClasses="mb-4"
      addInputClasses="custom-textarea mb-4"
    />
    <CAlert class="mb-5 w-700 font-sm" color="info">
      <div class="d-flex align-items-baseline text-black">
        <div>
          <CIcon name="infoCircle" size="custom-size" class="mr-2" :height="15" style="transform: rotate(180deg)" viewBox="0 0 32 32" />
        </div>
        <p class="mb-0">
          {{ $t('audience-new.config-import.is-phone-same-country-info') }}
        </p>
      </div>
    </CAlert>
    <div>
      <span class="mb-2 d-block font-weight-semi-bold">{{ $t('audience-new.config-import.is-first-row-header') }}</span>
      <div class="d-flex gap-15 align-items-center mb-5">
        <div class="d-flex align-items-center min-w-100">
          <CSwitch :checked.sync="isFirstLineHeaders" :disabled="isLoadingStep" class="switch-custom" color="primary" shape="pill" size="lg" />
          <span class="ml-2 align-self-center">{{
            isFirstLineHeaders ? $t('audience-new.config-import.manual.switch.yes') : $t('audience-new.config-import.manual.switch.no')
          }}</span>
        </div>
        <div class="border font-xs p-2 min-w-290 d-flex">
          <span class="mr-2 align-self-center">{{ $t('audience-new.config-import.sample.example') }}</span>
          <span>
            {{ $t('audience-new.config-import.sample.name') }}{{ delimiterChar }} {{ $t('audience-new.config-import.sample.contact')
            }}{{ delimiterChar }} E-mail <br />
            {{ $t('audience-new.config-import.sample.name-label') }}{{ delimiterChar }} 351567879065{{ delimiterChar }}
            {{ $t('audience-new.config-import.sample.email-label') }}
          </span>
        </div>
      </div>
    </div>
    <div class="mb-5">
      <span class="mb-2 d-block font-weight-semi-bold">{{ $t('audience-new.config-import.is-blacklist') }}</span>
      <div class="d-flex gap-15 align-items-center">
        <div class="d-flex align-items-center min-w-100">
          <CSwitch
            :checked.sync="isBlackList"
            :disabled="isLoadingStep"
            class="switch-custom"
            color="primary"
            shape="pill"
            size="lg"
            @update:checked="handleIsBlackListUpdate"
          />
          <span class="ml-2">{{ isBlackList ? $t('audience-new.config-import.switch.yes') : $t('audience-new.config-import.switch.no') }}</span>
        </div>
        <div class="d-flex gap-15">
          <div class="d-flex checkbox-area">
            <CInputCheckbox custom :checked.sync="blackListChannels.email" :disabled="!isBlackList || isLoadingStep" class="mr-2" />
            <span>E-mails</span>
          </div>
          <div class="d-flex checkbox-area">
            <CInputCheckbox custom :checked.sync="blackListChannels.sms" :disabled="!isBlackList || isLoadingStep" class="mr-2" />
            <span>SMS</span>
          </div>
        </div>
      </div>
      <div class="mt-3 d-flex align-items-baseline font-sm">
        <div>
          <CIcon name="infoCircle" size="custom-size" class="mr-2" :height="16" style="transform: rotate(180deg)" viewBox="0 0 32 32" />
        </div>
        <p class="mb-0 max-w-633">
          {{ $t('audience-new.config-import.is-blacklist-info') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import services from '@/services.js'
import { v4 as uuidv4 } from 'uuid'
import { IMPORT_CONTACTS_STEPS } from '../../../constants'

export default {
  name: 'ConfigImportManual',
  data() {
    return {
      importValue: undefined,
      isFirstLineHeaders: false,
      isLoadingStep: false,
      delimiterChar: ',',
      IMPORT_CONTACTS_STEPS,
      isBlackList: false,
      blackListChannels: {
        sms: false,
        email: false,
      },
    }
  },
  computed: {
    textAreaPlaceholder() {
      return `${this.$t('audience-new.config-import.sample.example')}
${this.$t('audience-new.config-import.manual.textarea.placeholder.header')}
${this.$t('audience-new.config-import.manual.textarea.placeholder.row')}
      `
    },
    organizationId() {
      return this.$route.params.organizationId
    },
    importValueTreated() {
      if (!this.importValue) return

      const separatedLines = this.importValue.trim().split(/\r?\n|\r|\n/g)
      const cleanedArray = separatedLines.filter((str) => str !== '')

      const rows = cleanedArray.map((row) => row.split(',').map((col) => col.trim()))

      const groupedRows = rows[0].map((_, colIndex) => rows.map((row) => row[colIndex]))

      const importValueTreated = groupedRows.map((row) => {
        return {
          id: uuidv4(),
          lines: row,
        }
      })

      return importValueTreated
    },
    isReadyToBeValidStep() {
      return this.importValue?.length > 0
    },
  },
  methods: {
    async sendFile() {
      try {
        this.isLoadingStep = true

        let subscriptionChannels = ['EMAIL', 'SMS']
        if (this.isBlackList) {
          subscriptionChannels = subscriptionChannels.filter((channel) => {
            if (this.blackListChannels.email && channel === 'EMAIL') return false
            if (this.blackListChannels.sms && channel === 'SMS') return false
            return true
          })
        }

        const response = await services.sendTable(this.organizationId, {
          ManualFile: this.importValueTreated,
          isFirstLineHeaders: this.isFirstLineHeaders,
          phoneCountryIndicator: 0, // * Cliente lida com indicador de país no arquivo
          contactsAreSubscribed: true,
          Subscriptions: subscriptionChannels,
        })

        this.$store.dispatch('audience/setImportedData', response.data.data)

        const { completedSteps, currentStep } = this.$store.state.audience.importContacts

        this.$store.dispatch('audience/setImportContactsCompletedSteps', [...completedSteps, currentStep])
        this.$store.dispatch('audience/setImportContactsStep', IMPORT_CONTACTS_STEPS.MATCH_COLUMNS)
        this.clearFormData()
      } catch (error) {
        const errorCode = error?.response?.data?.error?.code || error?.response?.data?.message || error?.response?.data?.messages[0]?.error?.code

        if (!errorCode) {
          this.$emit('addMessage', {
            status: 'danger',
            text: this.$t(`error.codes.DEFAULT`),
          })
          return
        }

        this.$emit('addMessage', {
          status: 'danger',
          text: this.$t(`general.errors.${errorCode}`),
        })
      } finally {
        this.isLoadingStep = false
      }
    },

    handleIsBlackListUpdate(isChecked) {
      if (isChecked) return

      this.blackListChannels = {
        sms: false,
        email: false,
      }
    },

    clearFormData() {
      this.isFirstLineHeaders = false
      this.importValue = undefined
      this.isBlackList = false
      this.blackListChannels = {
        sms: false,
        email: false,
      }
    },
  },
  watch: {
    isLoadingStep(newValue) {
      this.$emit('changeLoadingStep', newValue)
    },
    isReadyToBeValidStep: {
      handler(isValid) {
        if (!isValid) {
          const newValue = this.$store.state.audience.importContacts.validSteps.filter(
            (step) => step !== this.$store.state.audience.importContacts.currentStep,
          )
          return this.$store.dispatch('audience/setImportContactsValidSteps', newValue)
        }

        this.$store.dispatch('audience/setImportContactsValidSteps', [
          ...this.$store.state.audience.importContacts.validSteps,
          this.$store.state.audience.importContacts.currentStep,
        ])
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.line-height-1 {
  line-height: 1;
}
.gap-15 {
  gap: 15px;
}
.w-700 {
  width: 700px;
}
.min-w-100 {
  min-width: 100px;
}
.min-w-290 {
  min-width: 290px;
}
</style>

<style lang="scss">
.config-import-manual {
  .custom-textarea {
    height: 200px !important;
    overflow: auto;
  }
  .checkbox-area .custom-control-label::before {
    top: 0.13rem;
  }
  .checkbox-area .custom-control-label::after {
    top: 0.13rem;
  }
  .custom-radio-import .custom-control-label::before {
    width: 20px;
    height: 20px;
    top: 0.13rem;
  }
  .custom-radio-import .custom-control-label::after {
    width: 20px;
    height: 20px;
    top: 0.13rem;
  }
  .switch-custom span {
    background-color: var(--gray-300);
  }
}
</style>
